//  Angular imports
import { Component, Injector, OnDestroy, OnInit, afterNextRender, inject, signal, viewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
//  Application imports
import { ApplicationRoute } from 'app/models/application-route';
import { DeploymentService } from 'app/services/deployment.service';
import { environment } from 'environments/environment';
import { RAccountDetails } from 'app/records/account-details.record';
import { RActivatedStackInformation } from 'app/records/activated-stack-information.record';
import { RDeployment } from 'app/records/deployment.record';
import { RegionDisplay, RRegion, toDisplayRegion } from 'app/records/region.record';
import { Selectable } from 'app/dropdown-module/models/selectable';
import { Status } from 'app/records/status.record';
import { UserService } from 'app/services/user.service';
//  Third party imports
import { finalize, tap, takeUntil, switchMap, retry, share } from 'rxjs/operators';
import { Subject, forkJoin, Observable, Subscription, timer } from 'rxjs';
import { RDeploymentType, toDisplayDeploymentType } from 'app/records/deployment-type.record';
import { ProvisionState } from 'app/models/provision-state';
import { NgIf, NgFor, NgClass, NgSwitch, NgSwitchCase } from '@angular/common';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { ZFModalComponent } from '../../modal-module/zf-modal.component';
import { FormsModule } from '@angular/forms';
import { ZFDropdownComponent } from '../../dropdown-module/components/zf-dropdown.component';
import { PascalToHumanPipe } from '../../pipes/pascal-to-human.pipe';
import { SKUDisplayPipe } from '../../pipes/sku-display.pipe';

@Component({
    selector: 'zf-deployments-table',
    templateUrl: './deployments-table.component.html',
    styleUrls: ['./deployments-table.component.scss'],
    imports: [NgIf, LoadingIndicatorComponent, SidebarComponent, NgFor, NgClass, NgSwitch, NgSwitchCase, ZFModalComponent, FormsModule, ZFDropdownComponent, PascalToHumanPipe, SKUDisplayPipe]
})
export class DeploymentsTableComponent implements OnInit, OnDestroy {
    private router = inject(Router);
    private userService = inject(UserService);
    private deploymentsService = inject(DeploymentService);
    private injector = inject(Injector);

    public get isProduction(): boolean {
        return environment.production;
    }

    public readonly productName: string = environment.productName;
    public readonly Status: typeof Status = Status;
    public readonly ProvisionState: typeof ProvisionState = ProvisionState;
    public readonly regions: Array<Selectable> =
        Object.keys(RRegion)
            .filter(
                (key: string): boolean => typeof RRegion[key as keyof typeof RRegion] === 'string'
            )
            .map(
                (key: string): RegionDisplay => toDisplayRegion.get(RRegion[key as keyof typeof RRegion])
            )
            .filter(
                (val: RegionDisplay): boolean => {
                    if (!environment.production) {
                        // we are dev environment so we want to show it all anyways
                        return true;
                    }

                    // we are in PROD so we rely on the mapping to tell us
                    return val.isProdReady;
                }
            )
            .map(
                (val: RegionDisplay): Selectable => new Selectable(val.display, val.key)
            )
            .sort(
                (a: Selectable, b: Selectable) => (a.display < b.display) ? -1 : (a.display > b.display) ? 1 : 0
            );

    public readonly deploymentTypes: Array<Selectable> =
        Object.keys(RDeploymentType)
            .filter(
                (key: string): boolean => typeof RDeploymentType[key as keyof typeof RDeploymentType] === 'string'
            )
            .map(
                (key: string): Selectable => new Selectable(toDisplayDeploymentType.get(RDeploymentType[key as keyof typeof RDeploymentType]), RDeploymentType[key as keyof typeof RDeploymentType])
            );

    public readonly deployments = signal<Array<RDeployment>>([]);
    public readonly activatedStackInformation = signal<RActivatedStackInformation>(new RActivatedStackInformation());
    public readonly actionInProgress = signal<boolean>(false);
    public readonly targetDeployment = signal<RDeployment>(undefined);
    public readonly statusDeployment = signal<RDeployment>(undefined);
    public readonly currentState = signal<ProvisionState>(ProvisionState.PROVISION);
    public readonly apiError = signal<string>('');
    public readonly refreshing = signal<boolean>(false);
    public readonly ipV4 = signal<string>('');
    public readonly ipV6 = signal<string>('');

    private readonly refreshTimeout: number = 5000;
    private stopStatus$: Subject<void> = new Subject();
    private statusPoll$: Observable<RDeployment>;

    private readonly activationModal = viewChild<ZFModalComponent>('activationModal');
    private readonly deleteConfirmationModal = viewChild<ZFModalComponent>('deleteConfirmationModal');
    private readonly updateConfirmationModal = viewChild<ZFModalComponent>('updateConfirmationModal');
    private readonly statusModal = viewChild<ZFModalComponent>('statusModal');

    private get account(): RAccountDetails {
        return this.userService.accountSelected()?.details;
    }

    public ngOnInit(): void {
        this.activatedStackInformation().ipv4CIDRList = '0.0.0.0/0';
        this.activatedStackInformation().ipv6CIDRList = '::/0';
        this.ipV4.set('0.0.0.0/0');
        this.ipV6.set('::/0');
        if (this.account) {
            this.getDeployments();
        }
    }

    public ngOnDestroy(): void {
        this.stopStatus();
    }

    public selectTargetDeployment(event: MouseEvent, deployment: RDeployment) {
        if (this.targetDeployment()?.serialNumber === deployment.serialNumber) {
            // we already have a value
            // so we clear it
            this.targetDeployment.set(null);
            return;
        }

        this.targetDeployment.set(deployment);
    }

    public prepareActivation(event: MouseEvent): void {
        event.stopPropagation();

        if (!this.targetDeployment()?.isValid) {
            this.currentState.set(ProvisionState.PRE_REQUISITE_FAILURE);
        } else {
            this.activatedStackInformation.set(new RActivatedStackInformation());
            this.currentState.set(ProvisionState.PROVISION);
        }

        this.openModal(this.activationModal);
    }

    public prepareDelete(event: MouseEvent): void {
        event.stopPropagation();
        this.openModal(this.deleteConfirmationModal);
    }

    public prepareUpdate(event: MouseEvent, deployment: RDeployment): void {
        event.stopPropagation();
        this.targetDeployment.set(deployment);
        this.openModal(this.updateConfirmationModal);
    }

    public prepareStatus(event: MouseEvent, deployment: RDeployment): void {
        event.stopPropagation();
        this.statusDeployment.set(deployment);
        this.openModal(this.statusModal);
        this.stopStatus();
        this.statusPoll$ = timer(1, 10000)
            .pipe(
                switchMap(() => this.getDeployment(this.statusDeployment().serialNumber)),
                retry(),
                share(),
                takeUntil(this.stopStatus$)

            );
        this.statusPoll$.subscribe((dep: RDeployment) => {
            if (dep) {
                this.statusDeployment.set(dep);
                if (dep.information.status === Status.COMPLETE) {
                    this.stopStatus$.next();
                }
            } else {
                this.stopStatus$.next();
            }
        });
    }

    public prepareProvision(event: MouseEvent): void {
        event.stopPropagation();

        // apply filter after split to remove empty entries
        // non-empty string is truthy
        // trim the start and end of the string
        this.ipV4.update((ipV4: string): string => ipV4.trim());
        this.ipV6.update((ipV6: string): string => ipV6.trim());
        const activatedStackInformation: RActivatedStackInformation = this.activatedStackInformation();
        activatedStackInformation.ipv4CIDRList = this.ipV4().split('\n').filter(i => i).join(',');
        activatedStackInformation.ipv6CIDRList = this.ipV6().split('\n').filter(i => i).join(',');

        // validate we have correct info
        if (!activatedStackInformation.validate()) { return; }

        this.currentState.set(ProvisionState.CONFIRMATION);
    }

    public stopStatus(): void {
        // cancel any running polls
        if (this.statusPoll$) {
            this.stopStatus$.next();
        }
    }

    public provisionRegion(): string {
        return toDisplayRegion.get(this.activatedStackInformation().region as RRegion).display;
    }

    public provisionDeploymentType(): string {
        return toDisplayDeploymentType.get(this.activatedStackInformation().deploymentType);
    }

    public editProvision(event: MouseEvent): void {
        event.stopPropagation();
        this.currentState.set(ProvisionState.PROVISION);
    }

    public closeProvision(event: MouseEvent): void {
        event.stopPropagation();
        this.activationModal().toggle();
    }

    public confirmDeployment(event: MouseEvent): void {
        event.stopPropagation();

        this.actionInProgress.set(true);

        // add new entries
        this.deploymentsService.activate(this.account.accountId, this.targetDeployment().serialNumber, this.activatedStackInformation())
            .pipe(
                finalize(
                    (): void => {
                        this.actionInProgress.set(false);
                        const state: ProvisionState = this.currentState();
                        if (state === ProvisionState.NOT_APPROVED ||
                            state === ProvisionState.PRE_REQUISITE_FAILURE ||
                            state === ProvisionState.BLOCKED_BY_REQUEST) {
                            // we need to show the NOT APPROVED content
                            // so don't close anything
                        } else {
                            this.activationModal().toggle();
                            this.targetDeployment.set(null);
                        }
                    }
                )
            )
            .subscribe({
                next: (): Subscription => this.getDeployment(this.targetDeployment().serialNumber).subscribe(),
                error: (response: HttpErrorResponse): void => {
                    if (response.status === 422) {
                        // we have a bad entitlement
                        this.currentState.set(ProvisionState.PRE_REQUISITE_FAILURE);
                    }

                    if (response.status === 403) {
                        // we have a forbidden activate
                        // this is a poc
                        this.currentState.set(ProvisionState.NOT_APPROVED);
                    }

                    if (response.status === 400) {
                        // some error has happened
                        this.currentState.set(ProvisionState.BLOCKED_BY_REQUEST);
                        this.apiError.set(response.error);
                    }

                    console.error(response.status, response.message);
                }
            });
    }

    public manage(): void {
        if (!this.targetDeployment()) { return; }
        this.router.navigateByUrl(`${ApplicationRoute.ENTITLEMENTS}/${this.targetDeployment().serialNumber}`);
    }

    public getDeployments(): void {
        this.refreshing.set(true);
        forkJoin([
            timer(this.refreshTimeout),
            this.deploymentsService.getDeployments(this.account.accountId)
        ])
            .pipe(
                finalize(
                    (): void => {
                        this.refreshing.set(false);
                    }
                )
            )
            .subscribe(
                // eslint-disable-next-line  @typescript-eslint/no-unused-vars
                ([_, deployments]: [number, Array<RDeployment>]): void => {
                    if (!deployments || !deployments.length) {
                        this.router.navigateByUrl(ApplicationRoute.PRODUCT_INFORMATION);
                    } else {
                        this.deployments.set(deployments);
                    }
                }
            );
    }

    public deleteDeployment(): void {
        if (!this.targetDeployment()) { return; }
        this.actionInProgress.set(true);

        this.deploymentsService.deleteDeployment(this.account.accountId, this.targetDeployment().serialNumber)
            .pipe(
                finalize(
                    (): void => {
                        this.deleteConfirmationModal().toggle();
                        this.targetDeployment.set(null);
                        this.actionInProgress.set(false);
                    }
                )
            )
            .subscribe({
                next: (): Subscription => this.getDeployment(this.targetDeployment().serialNumber).subscribe(),
                error: (error: HttpErrorResponse): void => {

                    console.error(error.message);
                }
            });
    }

    public updateDeployment(): void {
        if (!this.targetDeployment()) { return; }
        this.actionInProgress.set(true);

        // force update on SKU
        // all the other elements will stay the same
        const activateInfo = new RActivatedStackInformation();
        activateInfo.shouldUpdateSKU = true;

        this.deploymentsService.update(this.account.accountId, this.targetDeployment().serialNumber,
            activateInfo)
            .pipe(
                finalize(
                    (): void => {
                        this.updateConfirmationModal().toggle();
                        this.targetDeployment.set(null);
                        this.actionInProgress.set(false);
                    }
                )
            )
            .subscribe({
                next: (): Subscription => this.getDeployment(this.targetDeployment().serialNumber).subscribe(),
                error: (error: HttpErrorResponse): void => {

                    console.error(error.message);
                }
            });
    }

    public titleMessage(deployment: RDeployment): string {
        if (this.isDeploymentInWarningState(deployment)) {
            return 'Your instance will expire in less than 90 days';
        } else if (this.isDeploymentInExpiringState(deployment)) {
            return 'Your instance will expire in less than 30 days';
        }

        return '';
    }

    public isDeploymentInWarningState(deployment: RDeployment) {
        if (deployment?.entitlement?.compute?.expiryDays > 30 && deployment?.entitlement?.compute?.expiryDays <= 90) {
            return true;
        }

        return false;
    }

    public isDeploymentInExpiringState(deployment: RDeployment) {
        if (deployment?.entitlement?.compute?.expiryDays > 0 && deployment?.entitlement?.compute?.expiryDays <= 30) {
            return true;
        }

        return false;
    }

    // The modals sit behind *ngIf on targetDeployment / statusDeployment, so the one
    // being opened may only exist once the signal written just before this call has
    // been rendered. afterNextRender runs after that render, and schedules one if
    // nothing else has.
    private openModal(modal: () => ZFModalComponent): void {
        afterNextRender((): void => modal()?.toggle(), { injector: this.injector });
    }

    private getDeployment(serialNumber: string): Observable<RDeployment> {
        return this.deploymentsService.getDeployment(this.account.accountId, serialNumber)
            .pipe(
                tap(
                    (deployment: RDeployment): void => {
                        this.deployments.update((deployments: Array<RDeployment>): Array<RDeployment> =>
                            deployments.map(
                                (_deployment: RDeployment): RDeployment =>
                                    _deployment.serialNumber.toLowerCase() === serialNumber.toLowerCase() ? deployment : _deployment
                            )
                        );
                    }
                )
            );
    }
}
